import type { RequestStatus } from "@/shared/api/types";

/** Sentinel for an empty status filter. It is not sent to the API. */
export const ALL_STATUSES = "ALL";

/** The statuses the queue can filter on, in ascending sort order. */
export const QUEUE_STATUSES = [
	"PENDING",
	"REOPENED",
	"APPROVED",
	"DECLINED",
] as const satisfies readonly RequestStatus[];

const SORTS = ["name", "status", "createdAt", "updatedAt"] as const;

export type QueueSort = (typeof SORTS)[number];

type QueueOrder = "ASC" | "DESC";

export function parseQueueSort(value: string | null): QueueSort {
	return SORTS.includes(value as QueueSort)
		? (value as QueueSort)
		: "createdAt";
}

export function parseQueueOrder(value: string | null): QueueOrder {
	return value === "ASC" ? "ASC" : "DESC";
}

/**
 * Multi-select → API filter.
 * `undefined` means every status. Choosing "all" clears a current filter;
 * choosing a status while "all" is selected replaces it.
 */
export function nextStatusFilter(
	current: RequestStatus[] | undefined,
	selected: string[],
): RequestStatus[] | undefined {
	const picked = QUEUE_STATUSES.filter((status) => selected.includes(status));
	const includesAll = selected.includes(ALL_STATUSES);
	const hadFilter = (current?.length ?? 0) > 0;

	if (selected.length === 0 || !includesAll) {
		return picked.length ? picked : undefined;
	}

	if (!hadFilter) {
		return picked.length ? picked : undefined;
	}

	return undefined;
}

/** First click on a column picks a direction; the next click flips it. */
export function toggleQueueSort(
	current: { sort?: QueueSort; order?: QueueOrder },
	column: QueueSort,
): { sort: QueueSort; order: QueueOrder } {
	const sort = current.sort ?? "createdAt";
	const order = current.order ?? "DESC";

	if (sort !== column) {
		const newestFirst = column === "createdAt" || column === "updatedAt";

		return { sort: column, order: newestFirst ? "DESC" : "ASC" };
	}

	return { sort: column, order: order === "ASC" ? "DESC" : "ASC" };
}
